import React from 'react';
import { useState, useEffect, useFormContext, Hooks, Code, Form, Field, Name, Title, Info, Actions, Toast, API, Utility, Button, Files, Control, Messages, OptionType } from 'helpers/FormImports';
import { Remittance } from 'models/RemittanceModel';
import { Billing } from 'models/BillingModel';
import { FileUpload } from 'components/common/FileUpload';
import { useDocumentLocal } from 'hooks/imageHooks/useDocLocal';
import { Extract } from 'helpers/ExtractHelper';
import { HLInkData } from 'models/BaseModel';
import RemittanceList from './RemittanceList';

const RemittanceForm: React.FC = () => {
  const width = { sno: 12, hsc: 8, diag: 8, client: 16, doctor: 16, status: 15, date: 15, amount: 8 };
  const [formId, formName] = [Code.Form.Id.REMITTANCE, Code.Form.Name.REMITTANCE];
  const {keyId, activeId, setActiveId, loading, setLoading, message, setMessage, showMenu, setShowMenu, confirm, setConfirm, error, setError, menuPosition, setMenuPosition, contextMenuRef, iframeRef, formRef } = useFormContext();
  const [activeTab, setActiveTab] = useState<string>(Utility.Storage.GetTab(formName));
  const [formData, setFormData] = useState<Remittance>({});
  const [statusOptions, setStatusOptions] = Hooks.Form.Options();
  const [billingList, setBillingList] = useState<Billing[]>([]); 
  const [statusId, setStatusId] = useState<number>(Code.Option.Billing.TOSUBMIT);
  const [selectAllChecked, setSelectAllChecked] = useState<boolean>(false);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [localRemittanceId, setLocalRemittanceId] = useState<number>(Code.Option.Common.NA); 
  const [remittanceData, setRemittanceData] = useState<HLInkData[]>([]); 
  const [remittanceCount, setRemittanceCount] = useState<number>(0); 

  const [hlinkDate, setHLinkDate] = useState<string>(new Date().toISOString().slice(0, 10));

  const [fileName, setFileName] = useState<string | undefined>(undefined);
  const [isFileAttached, setIsFileAttached] = useState(false);
  const [originalFileName, setOriginalFileName] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string | null>(null);
  const { handleLocalChange, handleLocalAttach, handleLocalDelete } = useDocumentLocal(fileName, setFileName, setIsFileAttached, setError,  setOriginalFileName, setFileContent);
 
  Form.Click.Outside([contextMenuRef],[() => setShowMenu(false),() => {}],true);
  const Privilege = Hooks.Privilege.List(formId);
  Hooks.Privilege.HasOpen(Privilege);
  
  useEffect(() => {
    Files.Json.Http(Code.File.REMITTANCE+Code.File.STATUS, Code.Option.Common.NA, setStatusOptions, setError);
    
    API.Data.GetByKeyId<Remittance>(formName, keyId, setLoading, setError)
      .then(async(data) => { if(data) {
        setActiveId(data.remittance_active); 

        const isValid = await Utility.isValid.Local(data.remittance_file);
        setFileName(data.remittance_file);
        setIsFileAttached(isValid);
        setLocalRemittanceId(data.remittance_id || 0)
        const formData = await Form.Map.Data(data);
        setFormData((prevData) => ({
          ...prevData,
          ...formData,
        }));
        callBillingList(data.remittance_id);

        //console.log("remittance-data--:", isValid,  data);

      }})
      setLoading(false);
  }, []);

  const callBillingList = async (remittanceId: number | undefined) => {
      if(!remittanceId) return;
      const url = API.Build.FormApiEndpint(Code.Form.Name.BILLING, 500, Name.Billing.Id, Code.Option.Sort.DESC, {
        fields: Utility.Join.Fields([
          Name.Billing.Id, 
          Name.Billing.Number, 
          Name.Billing.HscName, 
          Name.Billing.Diagnosis1Name, 
          Name.Client.FullName, 
          Name.Doctor.FullName, 
          Name.Common.StatusName, 
          Name.Billing.BillingDate, 
          Name.Billing.Amount
        ]), 
        [Name.Remittance.Id]: remittanceId,
       });
      ////console.log("Billing List URL:", url);
      await API.Data.List<Billing>(url, setBillingList, setError, setLoading);
   };

  const onSubmit = async (e: React.FormEvent) => {

    console.clear();
    
    if (!Toast.Validate.Form(e,setMessage)) return;
    if(!fileName){
      Utility.Message.Warning(["A remittance file is required before this record can be saved."],setMessage);
      return;
    }

    const addonData = await API.Data.AddonData(formData, formName, keyId);
    const finalData = Utility.Sanitize.FormData({
      ...formData,
      ...addonData,
      remittance_count: remittanceCount,
      remittance_file: fileName,
      remittance_name: originalFileName,
      remittance_data: fileContent,
    });

    //console.log("remittance-finalData----1-->",remittanceCount,fileName,originalFileName, fileContent);
    //console.log("remittance-finalData----2-->",finalData);

    if (keyId === Code.Key.NEW) {
      const response = await API.Data.Save(formName, keyId, finalData, setMessage, true, Code.Form.PopupClose.NONE);
      if(response.data){
        const newRemittanceId =  Extract.Key.Decode(response.data?.newid);
        if(newRemittanceId > Code.Option.Common.NA) {
          setLocalRemittanceId(newRemittanceId);
            UpdateBillingStatus();
        }
      }
    }
    else{
      const response = await API.Data.Save(formName, keyId, finalData, setMessage);
      //console.log("save-response-----",response)
      if(response){
        UpdateBillingStatus();
      }
    }
  };


  useEffect(() =>{
    if(fileContent) {
      let count:number = 0;
      const lines = fileContent.split(/\r?\n/);  //split by line
      for (const line of lines) {
        if (!line.includes("HEADER") && !line.includes("TRAILER") && line.length=== 234) {
          count++;
        }
      }
      setRemittanceCount(count);
    }
  },[fileContent]);

 

  const UpdateBillingStatus = async () => {
    if(fileContent && localRemittanceId>0) {
      const lines = fileContent.split(/\r?\n/);  //split by line
      const hlinkData: HLInkData[] = [];
      for (const line of lines) {
        if (line.includes("HEADER")) {
          const date = Utility.Format.SafeDate(line.substring(0, 19));
          setHLinkDate(date);
        } else if (line.includes("TRAILER")) {
          const trailer = line.substring(0, 27);
        } else {
          if (line.length === 234) {
            const HLinkLine: HLInkData = {
              status: line.substring(24, 25).trim(),
              billno: line.substring(26, 33).trim(),
              paid: insertDecimal(line.substring(91, 95), 2),
              amount: insertDecimal(line.substring(100, 104), 2),
              expkey: line.substring(105, 109),
            };
            hlinkData.push(HLinkLine);
          }
        }
      }
      //console.log("hlinkData---->",hlinkData);
      setRemittanceData(hlinkData);
    }
  }

  useEffect(()=>{
    //console.log("hlinkDate:",hlinkDate);
  },[hlinkDate])

  useEffect(() => {
    if (remittanceData && remittanceData.length > 0) {
      (async () => {
        const billnoList = remittanceData.map(item => item.billno).join(',');
        
        if (billnoList && billnoList.length) {
          const url = API.Build.FormApiEndpint(Code.Form.Name.BILLING, 200, Name.Billing.Id, Code.Option.Sort.DESC, {
            fields: Utility.Join.Fields([
              Name.Billing.Id,
              Name.Billing.Number 
            ]), 
            [Name.Billing.Number]: `in:${billnoList}`
          });

          const dataList = await API.Data.return<Billing>(url, setError, setLoading);
          //console.log("biling-data---->",dataList);
          if(dataList){
            if(dataList.length == remittanceData.length){
              const batchData: any[] = [];
              remittanceData.forEach(data => {
                const billingRow = dataList.find(item => item.billing_number === data.billno);
                if(billingRow){
                  const billingId = billingRow.billing_id
                  //console.log(hlinkDate);
                  if(billingId && billingId>0){
                      const item: any = {
                      id: billingId,
                      status_id: getStatusId(data.status,data.paid),
                      remittance_id: localRemittanceId,
                      remittance_date: hlinkDate
                    };
                    batchData.push(item);
                  }
                }
              });
              if (batchData.length > 0) {
                await API.Data.Batch(Code.Form.Name.BILLING, batchData);
              }
            }
          }
        }
      })();
    }
  }, [remittanceData]);

  useEffect(() => {
    // if(localRemittanceId>Code.Option.Common.NA) {
    //   UpdateBillingStatus();
    // }
  }, [localRemittanceId]);

  function insertDecimal(value: string, position: number): string {
    return value.slice(0, position) + '.' + value.slice(position);
  }

  function getStatusId(status: string | undefined, paid: string | undefined): string {
    let StatusId = Code.Option.Billing.INPROCESS;
    if(status && paid) {
      if (status === "A") {
        StatusId = (paid === "00.00") ? Code.Option.Billing.REFUSED : Code.Option.Billing.PAID;
      } else if (status === "R") {
        StatusId = Code.Option.Billing.REFUSED;
      } else if (status === "H") {
        StatusId = Code.Option.Billing.HOLD;
      }
    }
    return StatusId.toString();
  }

  return (
    <Control.Form.Frame setMenuPosition={setMenuPosition} setShowMenu={setShowMenu}>
      <Messages.Toast Messages={message} Error={error} />
      <Messages.Confirm Messages={confirm} onConfirm={() => API.Data.Delete(formName, keyId, activeId, setMessage)} onRefuse={()=>{}} />
      {!loading && (
        <form id={Code.Form.Name.EDIT} ref={formRef} onSubmit={onSubmit} autoComplete="off">
          <Button.Save keyId={keyId} activeId={activeId} handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)}  formName={formName} Privilege={Privilege} />
          <Control.Tab.Header titles={["Details"]} activeTab={activeTab} formName={formName} setActiveTab={setActiveTab} />
          <Control.Form.Data>
 
            <Control.Tab.Content id={Code.Tab.Tab1} isActive={activeTab === Code.Tab.Tab1}>
              <Control.Row>
                <Field.Title  size={[0,60,0]} />
                <Field.Date id={Name.Remittance.Date} value={formData.remittance_date} title={Title.Date} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,20,1]} currentDate={true} disabled={true} />
                <Field.Select id={Name.Common.StatusId} value={statusId} title={Title.Status} options={statusOptions} onChange={(e) => setStatusId(Form.Field.Value(e))} size={[1,20,1]} disabled={true} />
              </Control.Row>
              <Control.Form.Table>
                <Control.Form.Title>
                  <Control.SelectAll selectAllChecked={selectAllChecked} onSelectAll={(e) => Form.Select.AllIds(e, setSelectedItems, billingList, setSelectAllChecked,Code.Form.Name.BILLING)} extra={0} />
                  <Control.Form.Columns values={[
                    [Title.SNo, width.sno],
                    [Title.HSC, width.hsc],
                    [Title.Diag, width.diag],
                    [Title.Client, width.client],
                    [Title.Doctor, width.doctor],
                    [Title.State, width.status],
                    [Title.Date, width.date],
                    [Title.Amount, width.amount, Code.Align.RIGHT]
                  ]} />
                </Control.Form.Title>
                <Control.Form.List>
                  {billingList.map((item, index) => { return (
                    <Control.Form.Index key={index} index={index}>
                      <Control.Form.Item>
                        <Control.Grid.Check keyId={item.billing_id?.toString()} selectedItems={selectedItems} toggleSelection={Form.Select.Toggle} setSelectedItems={setSelectedItems} />
                        <Control.Grid.Items values={[
                          [item.billing_number, width.sno],
                          [item.billing_hsc_name, width.hsc],
                          [item.billing_diagnosis1_name, width.diag],
                          [Utility.Get.FullName(item, Code.Form.Name.CLIENT), width.client],
                          [Utility.Get.FullName(item, Code.Form.Name.DOCTOR), width.doctor],
                          [item.status_name, width.status],
                          [Utility.Format.DateOnly(item.billing_date), width.date],
                          [Utility.Format.Amount(item.billing_amount), width.amount, Code.Align.RIGHT],
                        ]} />
                      </Control.Form.Item>
                    </Control.Form.Index>
                  )})}
                </Control.Form.List>
              </Control.Form.Table>
              <Control.Row>
                <Field.Area id={Name.Remittance.Comment} value={formData.remittance_comment} title={Title.Comment} onChange={(e) => Form.Field.Change(e, formData, setFormData)} />
              </Control.Row>
              <Control.Box>
                <FileUpload isFileAttached={isFileAttached} handleFileChange={handleLocalChange} handleFileAttach={handleLocalAttach} handleFileDelete={handleLocalDelete} />
              </Control.Box>
            </Control.Tab.Content>
          </Control.Form.Data>
        </form>
      )}
      {showMenu && (<div ref={contextMenuRef}><Control.FormContextMenu keyId={keyId} activeId={activeId} showMenu={showMenu} formName={formName}  handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} setShowMenu={setShowMenu} Privilege={Privilege} pos={menuPosition} /></div>)}
      <Actions.Form />
    </Control.Form.Frame>
  );
};

export default RemittanceForm;